import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { BaseSyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import AlertBanner, { ALERT_VARIANT } from '@/components/common/AlertBanner/AlertBanner';
import Box from '@/components/common/Box/Box';
import ConfirmationButtons from '@/components/common/ConfirmationButtons/ConfirmationButtons';
import { FQ_BUTTON_COLOR_VARIANTS } from '@/components/muiCustomized/FQButton/constant';
import { AbsenceWithTypeData } from '@/domain/requestsPage';
import { MEDIA_QUERIES_BREAKPOINTS, PX_TO_REM } from '@/globalStyles';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import DeleteRequestButton from '@/pages/Requests/common/DeletRequestButton/DeleteRequestButton';
import LimitSummary from '@/pages/Requests/common/LimitSummary/LimitSummary';
import useLimitSummary from '@/pages/Requests/common/LimitSummary/useLimitSummary';
import RequestInfo from '@/pages/Requests/common/RequestInfo/RequestInfo';
import useRequestDetailsState from '@/pages/Requests/RequestsContent/RequestDetailsModal/useRequestDetailsState';
import { REQUEST_STATUS } from '@/utils/enums/requestsPage';

interface RequestDetailsContentProps {
    singleRequestDetails: AbsenceWithTypeData;
    credentialId: string;
    onEditClick: () => void;
    onCloseModal: () => void;
    getRequests: () => void;
    rejectDelete: () => void;
    acceptDelete: () => void;
    onUpdateAbsenceStatus: (
        id: string,
        status: REQUEST_STATUS,
        name: string | undefined,
        isDeletionRequest: boolean
    ) => (event: BaseSyntheticEvent) => void;
}

const RequestDetailsContent = ({
    singleRequestDetails,
    credentialId,
    onEditClick,
    onCloseModal,
    getRequests,
    rejectDelete,
    acceptDelete,
    onUpdateAbsenceStatus,
}: RequestDetailsContentProps) => {
    const { t } = useTranslation();
    const { isAdmin, isLead } = useLoggedInUserData();

    const { summary } = useLimitSummary(singleRequestDetails);

    const requestDetailsWithPreviewOverLimit = {
        ...singleRequestDetails,
        isOverLimit: summary?.isOverLimit ?? singleRequestDetails.isOverLimit,
    };

    const {
        isWaitingForAcceptance,
        isWaitingForDeletion,
        isOwnRequest,
        showDeletionButton,
        showAcceptanceButtons,
        showWaitingDeletionButton,
        allButtonsShown,
        isOverLimitAndNotAllowed,
        isOverLimitAndAllowed,
    } = useRequestDetailsState(requestDetailsWithPreviewOverLimit, credentialId);

    const { status, _id, hasPermissionForAcceptance } = singleRequestDetails;
    const isOverLimitAndNotAllowedWaiting = isWaitingForAcceptance && isOverLimitAndNotAllowed && (isAdmin || isLead);
    const isOverLimitAndAllowedWaiting = isWaitingForAcceptance && isOverLimitAndAllowed && (isAdmin || isLead);

    return (
        <>
            {isWaitingForAcceptance ? (
                <StyledBox
                    title={t('requests_common_request_info')}
                    headerRightPanel={<EditOutlinedIcon sx={{ cursor: 'pointer' }} onClick={onEditClick} />}
                >
                    <RequestInfo requestDetails={singleRequestDetails} />
                </StyledBox>
            ) : (
                <RequestInfo requestDetails={singleRequestDetails} />
            )}

            {isWaitingForAcceptance && <LimitSummary summary={summary} />}

            {isOverLimitAndNotAllowedWaiting && (
                <AlertBanner variant={ALERT_VARIANT.WARNING} content={t('overLimit_warning_message')} />
            )}

            {isOverLimitAndAllowedWaiting && (
                <AlertBanner variant={ALERT_VARIANT.INFO} content={t('overLimit_info_message')} />
            )}

            <ButtonsWrapper allButtonsShown={allButtonsShown}>
                {showDeletionButton && (
                    <DeleteRequestButton
                        getRequests={getRequests}
                        onCloseModal={onCloseModal}
                        absenceId={singleRequestDetails?._id}
                        absenceStatus={status}
                        requestName={singleRequestDetails?.typeData?.name ?? '-'}
                        isOwnRequest={isOwnRequest}
                        hasPermissionForAcceptance={hasPermissionForAcceptance}
                    />
                )}

                {showAcceptanceButtons && (
                    <ConfirmationButtons
                        cancelText={t('previewView_declineButton_title')}
                        submitText={t('previewView_acceptButton_title')}
                        cancelButtonColor={FQ_BUTTON_COLOR_VARIANTS.fqGrey}
                        onCancel={onUpdateAbsenceStatus(
                            _id,
                            REQUEST_STATUS.REJECTED,
                            singleRequestDetails?.typeData?.name,
                            isWaitingForDeletion
                        )}
                        onSubmit={onUpdateAbsenceStatus(
                            _id,
                            REQUEST_STATUS.ACCEPTED,
                            singleRequestDetails?.typeData?.name,
                            isWaitingForDeletion
                        )}
                        disabledSubmit={isOverLimitAndNotAllowed}
                    />
                )}

                {showWaitingDeletionButton && (
                    <ConfirmationButtons
                        onCancel={rejectDelete}
                        onSubmit={acceptDelete}
                        cancelText={t('previewView_declineButton_title')}
                        submitText={t('previewView_acceptButton_title')}
                        cancelButtonColor={FQ_BUTTON_COLOR_VARIANTS.fqGrey}
                    />
                )}
            </ButtonsWrapper>
        </>
    );
};

const StyledBox = styled(Box)`
    ${RequestInfo} {
        padding: 2rem 1.5rem;
    }
`;

const ButtonsWrapper = styled.div<{ allButtonsShown: boolean }>`
    display: flex;
    margin-top: ${PX_TO_REM[20]};
    width: 100%;
    justify-content: center;

    @media screen and (width <= ${MEDIA_QUERIES_BREAKPOINTS.mobile}px) {
        flex-direction: column;
    }

    @media screen and (width <= ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
        ${({ allButtonsShown }) =>
            allButtonsShown &&
            css`
                gap: ${PX_TO_REM[10]};
                flex-direction: column;
                & button {
                    width: 100%;
                }

                & > button:first-of-type {
                }
            `}
    }

    @media screen and (width > ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
        ${({ allButtonsShown }) =>
            allButtonsShown &&
            css`
                justify-content: space-between;
                & > button:first-of-type {
                    margin-right: ${PX_TO_REM[10]};
                }
            `}
    }
`;

export default RequestDetailsContent;
